import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import AudioService from 'services/ServiceAUDIOS';
import * as yup from 'yup';

/**
 * @typedef {Object} Audio
 * @property {number} id
 * @property {string} name
 * @property {string} description
 * @property {string} type
 */

/**
 * Hook para buscar áudios e agrupá-los por tipo.
 * Retorna todos os áudios e também uma versão agrupada por 'type'.
 * @returns
 */
const useAudios = () => {
  const [state, setState] = useState({
    /** @type {Audio[]} */
    response: [],
    isLoading: false,
    isError: false,
    error: null,
    totalRegisters: 0
  });

  const fetch = useCallback(async () => {
    setState((prevState) => ({
      ...prevState,
      isLoading: true,
      isError: false,
      error: null
    }));

    try {
      const apiResponse = await AudioService.findAll(false);
      const validatedResponse = await audioResponseSchema.validate(
        apiResponse.data,
        { abortEarly: false }
      );

      if (validatedResponse.status === 'OK' && validatedResponse.data) {
        const audioList = validatedResponse.data ?? [];
        const quantity = validatedResponse.totalRegisters ?? 0;

        setState((prevState) => ({
          ...prevState,
          response: audioList,
          totalRegisters: quantity,
          isLoading: false,
          isError: false,
          error: null
        }));
      } else {
        throw new Error(
          validatedResponse.message ||
            'Dados de áudios não encontrados ou inválidos.'
        );
      }
    } catch (err) {
      const errorMessage =
        err.name === 'ValidationError'
          ? err.errors.join(', ')
          : err.response?.data?.message || 'Erro ao carregar áudios';

      toast.error(errorMessage, {
        autoClose: 3000,
        closeOnClick: true
      });
      setState((prevState) => ({
        ...prevState,
        response: [],
        totalRegisters: 0,
        isLoading: false,
        isError: true,
        error: err
      }));
    }
  }, []);

  /**
   * Agrupa os áudios por tipo.
   * @type {Object.<string, Audio[]>}
   */
  const groupedByType = useMemo(() => {
    return state.response.reduce((acc, audio) => {
      if (!acc[audio.type]) {
        acc[audio.type] = [];
      }
      acc[audio.type].push(audio);
      return acc;
    }, {});
  }, [state.response]);

  const groupedByTypeOptions = useMemo(() => {
    return Object.entries(groupedByType).reduce((acc, [type, audios]) => {
      acc[type] = audios.map((audio) => ({
        label: audio.name,
        value: audio.id
      }));
      return acc;
    }, {});
  }, [groupedByType]);

  /**
   * Busca um áudio pelo ID e valida a resposta com o esquema Yup.
   * @param {string} id - O ID do áudio a ser buscado.
   * @returns {Promise<Audio|null>} O áudio encontrado ou null em caso de erro.
   */
  const fetchAudioById = useCallback(async (id) => {
    setState((prevState) => ({
      ...prevState,
      isLoading: true,
      isError: false,
      error: null
    }));
    try {
      const response = await AudioService.findById(id);
      const validatedResponse = await singleAudioResponseSchema.validate(
        response.data,
        { abortEarly: false }
      );

      if (validatedResponse.status === 'OK' && validatedResponse.data) {
        setState((prevState) => ({
          ...prevState,
          isLoading: false,
          isError: false,
          error: null
        }));
        return validatedResponse.data;
      } else {
        throw new Error(
          validatedResponse.message || 'Dados do áudio não encontrados.'
        );
      }
    } catch (err) {
      const errorMessage =
        err.name === 'ValidationError'
          ? err.errors.join(', ')
          : err.response?.data?.message || 'Erro ao carregar áudio por ID';

      toast.error(errorMessage, {
        autoClose: 3000,
        closeOnClick: true
      });
      setState((prevState) => ({
        ...prevState,
        isLoading: false,
        isError: true,
        error: err
      }));
      return null;
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return {
    ...state,
    fetch,
    fetchAudioById,
    groupedByType,
    groupedByTypeOptions
  };
};

export default useAudios;

const audioSchema = yup.object().shape({
  id: yup.number().integer().required('ID é obrigatório'),
  name: yup.string().required('Nome é obrigatório'),
  description: yup.string().required('Descrição é obrigatória'),
  type: yup.string().required('Tipo é obrigatório')
});

const audioResponseSchema = yup.object().shape({
  status: yup
    .string()
    .oneOf(['OK', 'ERROR'], 'Status inválido')
    .required('Status é obrigatório'),
  message: yup.string().nullable(),
  data: yup.array().of(audioSchema).required('Dados são obrigatórios'),
  errors: yup.array().of(yup.string()).defined('Erros são obrigatórios'),
  totalRegisters: yup
    .number()
    .integer()
    .required('Total de registros é obrigatório')
});

const singleAudioResponseSchema = yup.object().shape({
  status: yup
    .string()
    .oneOf(['OK', 'ERROR'], 'Status inválido')
    .required('Status é obrigatório'),
  message: yup.string().nullable(),
  data: audioSchema.nullable(),
  errors: yup.array().of(yup.string()).defined('Erros são obrigatórios')
});
